import { Component, Inject, InjectionToken, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HandleFacetsLockInStorage, ResourceFacetSelectedAction } from 'search/actions/facet.actions';
import { FacetState } from 'search/facets/models/facet-state';
import { CatalogDateFacet } from 'search/facets/models/resource-facet';
import { ComponentData } from 'search/models/filter-panel';
import { defaultCatalogDateFacets } from 'search/models/search-object';
import { CatalogDate } from 'search/models/search-results';
import { getAdvancedMode, State } from 'search/reducers/root.reducer';
import { copyObject } from 'search/reducers/utils';
import { randomNumericString } from 'shared/utils/random';

interface PeriodOption {
  title: string;
  value: string;
}

export const CATALOG_DATE_FACET_COMPONENT_DATA = new InjectionToken<{}>('DATE_FACET_COMPONENT_DATA');

@Component({
  selector: 'app-catalog-date-facet',
  templateUrl: './catalog-date-facet.component.html',
  styleUrls: ['./catalog-date-facet.component.scss'],
})
export class CatalogDateFacetComponent implements OnInit, OnDestroy {

  public set checked(checked: boolean) {
    const facet = {
      ...this.getCatalogDateFacets(),
      selected: checked
    };
    this.store.dispatch(new ResourceFacetSelectedAction(facet));
    if (checked && this.isAdvancedMode && !facet.isResetLocked) {
      this.store.dispatch(new HandleFacetsLockInStorage({facet}));
    }
    this._checked = checked;
  }

  public catalogDate: CatalogDate = null;
  public isLoading: Subject<boolean> = new Subject().pipe(debounceTime(300)) as Subject<boolean>;
  public isResetLocked = false;
  public expanded: boolean;
  public facetKey: string;
  public facetBlockName: string;
  public periodOptions: PeriodOption[];
  public _checked: boolean;
  public selectBoxId = `catalog-date-facet-select-${randomNumericString()}`;

  private isAdvancedMode: boolean = false;
  private subscriptions = new Subscription();

  constructor(
    private store: Store<State>,
    @Inject(CATALOG_DATE_FACET_COMPONENT_DATA) public componentData: ComponentData,
  ) {
  }

  public ngOnInit() {
    this.subscriptions.add(this.componentData.facetData
    .subscribe((data: FacetState<CatalogDateFacet>) => {
      const selectedDateFacet = data.selected;
      this.facetBlockName = data.schemaWithData.schema.blockName;
      this._checked = selectedDateFacet.selected;
      this.catalogDate = selectedDateFacet.catalogDate;
      this.isLoading.next(data.isLoading);
      this.isResetLocked = selectedDateFacet.isResetLocked;
      this.expanded = data.expanded;
    }));
    this.subscriptions.add(this.store.select(getAdvancedMode).subscribe((advanced) => {
      this.isAdvancedMode = advanced;
    }));

    this.facetKey = this.componentData.facetKey;
    this.periodOptions = this.makePeriodOptions();
  }

  public onCheckboxChange(checked: boolean) {
    this.checked = checked;
  }

  public onCatalogDateChange(value: CatalogDate) {
    this.catalogDate = value;
    this.checked = true;
  }

  public onToggleBubbleLock() {
    this.store.dispatch(new HandleFacetsLockInStorage({facet: this.getCatalogDateFacets()}));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getCatalogDateFacets(): CatalogDateFacet {
    const facets = copyObject(defaultCatalogDateFacets);

    if (this.catalogDate) {
      facets.catalogDate = this.catalogDate;
    }

    facets.isResetLocked = this.isResetLocked;
    facets.label = `${facets.catalogDate ? facets.catalogDate : ''}`;
    facets.id = facets.label;

    return facets;
  }

  private makePeriodOptions(): PeriodOption[] {
    return [
      {title: 'currentWeekPeriod', value: CatalogDate.WEEK},
      {title: 'currentMonthPeriod', value: CatalogDate.MONTH},
      {title: 'currentQuarterPeriod', value: CatalogDate.QUARTER},
      {title: 'currentYearPeriod', value: CatalogDate.YEAR},
    ];
  }
}
